import { useState } from 'react'
import { axiosInstance } from '../helpers'
import { toast } from 'react-toastify'
import FileDownload from 'js-file-download'

export interface MoveInSurveySummary {
    id: number
    apartment_id: number
    apartment_name: string
    inspection_count: number
    survey_count: number
    average_rating: number | null
    units_serviced: number
    units_ready: number
}

export interface SurveyAnswer {
    question: number
    question_text: string
    question_type: string
    value: string
}

export interface MoveInSurveyResponse {
    id: number
    rating: number
    comment: string
    created_date: string
    submitted_by_name: string
    submitted_by_email: string
    unit_name: string
    area_name: string
    service_status: string
    answers: SurveyAnswer[]
}

export interface MoveInSurveyDetail {
    apartment_id: number
    apartment_name: string
    inspection_count: number
    survey_count: number
    average_rating: number | null
    surveys: MoveInSurveyResponse[]
}

export interface MoveInSurveyAiSummary {
    overall_sentiment: 'positive' | 'mixed' | 'negative'
    summary: string
    strengths: string[]
    concerns: string[]
    recommendation: string
}

export interface DateFilterParams {
    start_date?: string
    end_date?: string
}

export const useMoveInSurvey = () => {
    const [loading, setLoading] = useState(false)
    const [summaryList, setSummaryList] = useState<MoveInSurveySummary[]>([])
    const [detail, setDetail] = useState<MoveInSurveyDetail | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [aiSummary, setAiSummary] = useState<MoveInSurveyAiSummary | null>(
        null,
    )
    const [aiSummaryLoading, setAiSummaryLoading] = useState(false)

    const getPortfolioSummary = (
        organization?: number,
        dateParams?: DateFilterParams,
    ) => {
        setLoading(true)
        axiosInstance
            .get('survey/move-in-survey/portfolio_summary/', {
                params: {
                    organization: organization ?? undefined,
                    ...dateParams,
                },
            })
            .then((res) => {
                const data = (res.data as Omit<
                    MoveInSurveySummary,
                    'id'
                >[]).map((item) => ({ ...item, id: item.apartment_id }))
                setSummaryList(data)
            })
            .catch(() => {
                toast.error('Failed to load survey summary.')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const getApartmentDetail = (
        apartmentId: number,
        dateParams?: DateFilterParams,
    ) => {
        setDetailLoading(true)
        setDetail(null)
        axiosInstance
            .get('survey/move-in-survey/apartment_detail/', {
                params: { apartment_id: apartmentId, ...dateParams },
            })
            .then((res) => {
                setDetail(res.data)
            })
            .catch((err) => {
                const key = err?.response?.data?.key
                if (key === 'APARTMENT_NOT_FOUND') {
                    toast.error('Property not found or you do not have access.')
                } else if (
                    key === 'APARTMENT_ID_REQUIRED' ||
                    key === 'INVALID_APARTMENT_ID'
                ) {
                    toast.error('Invalid property selection.')
                } else {
                    toast.error('Failed to load survey details.')
                }
            })
            .finally(() => {
                setDetailLoading(false)
            })
    }

    const exportApartmentDetail = async (
        apartmentId: number,
        apartmentName: string,
        dateParams?: DateFilterParams,
    ) => {
        try {
            const res = await axiosInstance.get(
                'survey/move-in-survey/apartment_export/',
                {
                    params: { apartment_id: apartmentId, ...dateParams },
                    responseType: 'blob',
                },
            )
            FileDownload(
                new Blob([res.data]),
                `${apartmentName}_survey_results.xlsx`,
            )
        } catch {
            toast.error('Failed to export survey data.')
        }
    }

    const getAiSummary = async (
        apartmentId: number,
        dateParams?: DateFilterParams,
    ) => {
        setAiSummaryLoading(true)
        setAiSummary(null)
        try {
            const res = await axiosInstance.get(
                'survey/move-in-survey/apartment_ai_summary/',
                { params: { apartment_id: apartmentId, ...dateParams } },
            )
            setAiSummary(res.data.ai_summary)
        } catch (err: any) {
            const key = err?.response?.data?.key
            if (key === 'NO_SURVEYS') {
                toast.info('No surveys available to analyze for this property.')
            } else if (key === 'AI_SUMMARY_FAILED') {
                toast.error(
                    'AI analysis is temporarily unavailable. Please try again later.',
                )
            } else {
                toast.error('Failed to generate AI summary.')
            }
        } finally {
            setAiSummaryLoading(false)
        }
    }

    return {
        loading,
        summaryList,
        setSummaryList,
        detail,
        setDetail,
        detailLoading,
        aiSummary,
        setAiSummary,
        aiSummaryLoading,
        getPortfolioSummary,
        getApartmentDetail,
        exportApartmentDetail,
        getAiSummary,
    }
}
